/**
 * Подключение контроллера боя к оболочке: стор, окна, команды HUD. Одна функция — вызывает `main.tsx`.
 */
import { applyPurchase } from "@newfungame/engine";
import { activeBalance } from "../app/balance";
import type { AdminToolRequest, DailyTaskClaim, PlayerState, WaveLog, ShopItem } from "@newfungame/contracts";
import {
  addCoinsInFlight,
  appStore,
  clearCoinsInFlight,
  markCoinsShort,
  openWindow,
  retryLockOf,
  serverNow,
  setBoostOffer,
  setPlayer,
  speedOpen,
  adminTools,
  syncRevivePicks,
  vipActive,
  type AppState,
} from "../app";
import { analytics } from "../analytics";
import type { ApiTransport } from "../api";
import { connectTabs, optimisticPurchase } from "../ui/tabs";
import { createSync, type Sync, type WaveSyncEvent } from "../sync";
import { connectHud } from "../ui/hud";
import { setWindowActions, windowActions } from "../ui/windows/actions";
import { createGameController, type GameController } from "./controller";
import { createLiveSender } from "../live";
import { currentWindow } from "../app/store";
import { setBetaGame } from "../app/beta-slot";
import type { LiveUi } from "@newfungame/contracts";

/** Админ шлёт журнал в 10 раз раньше длины волны: сервер принимает такой темп только у него, x10 укладывается. */
const ADMIN_PACE_SCALE = 0.1;

export interface GameOptions {
  /** Транспорт API: журналы волн, покупки и клеймы уходят на сервер (I2). Без него — локальная игра (`?fake=1`). */
  api?: ApiTransport;
  /** Журнал завершённой волны (e2e, отладка) — помимо отправки на сервер. */
  onWaveLog?(log: WaveLog): void;
  /** Волна пройдена: `app/asks.ts` решает, о чём спросить игрока. */
  onWaveCleared?(wave: number, boss: boolean): void;
  /** Доля длины волны до отправки журнала (мок без сервера — 0). */
  pace?: number;
  /** Сервер не поддерживает версию баланса клиента — по умолчанию перезагрузка страницы. */
  reload?(): void;
}

/** Бой + синхронизация: источник снимков и то, что нужно вызвать при смене сети. */
export interface GameConnection extends GameController {
  sync: Sync | null;
}

/** Запустить настоящий бой: вернуть источник снимков для цикла кадров. */
export function connectGame(opts: GameOptions = {}): GameConnection {
  let sync: Sync | null = null;
  // `game` создаётся ниже, а синхронизации он нужен в колбэках — замыкание на переменную
  let gameRef: GameController | null = null;
  /** Убийства сданных волн за сессию; к ним добавляется текущая волна (`wave.killed`) — число для «I crushed N zombies». */
  let loggedKills = 0;
  const onWaveEvent = (e: WaveSyncEvent): void => {
    if (e === "acked" || e === "rejected") {
      // журналов в пути не осталось: монеты волн у сервера, их можно тратить
      if (sync?.pending() === 0) clearCoinsInFlight();
      gameRef?.synced();
    } else gameRef?.syncOffline();
    // откат заметен: монеты и волна вернулись назад, игрок должен знать почему
    if (e === "rejected") openWindow({ kind: "runEnded", rejected: true });
  };
  const game = createGameController({
    balance: activeBalance(),
    getPlayer: () => appStore.get().player,
    setPlayer: (p: PlayerState) => setPlayer(p),
    setBoostOffer,
    openWindow: (w) => openWindow(w),
    onWaveCleared: (wave, boss) => opts.onWaveCleared?.(wave, boss),
    awaitServerSeed: opts.api !== undefined,
    onCoinsCredited: (coins) => addCoinsInFlight(coins),
    onWaveStart: () => sync?.waveStarted(),
    onWaveLog: (log) => {
      loggedKills += log.result.kills;
      opts.onWaveLog?.(log);
      sync?.submitWave(log);
    },
    clientVersion: "client-" + (import.meta.env.MODE ?? "dev"),
    autoPref: () => appStore.get().autoPref,
    serverNow: () => serverNow(appStore.get()),
  });
  gameRef = game;
  setBetaGame(game);
  // живой эфир (MADR 0040): текущая волна и экран игрока уходят зрителю; ошибки сети молча пропускаем
  const api = opts.api;
  const live = api
    ? createLiveSender({ send: (chunk) => void api.call("live", chunk).catch(() => {}), now: Date.now })
    : null;
  const liveUpdate = () => live?.update(game.live(), liveUi(appStore.get()));
  if (live) appStore.subscribe(liveUpdate);
  if (opts.api) {
    sync = createSync({
      api: opts.api,
      getPlayer: () => appStore.get().player,
      setPlayer,
      onWave: onWaveEvent,
      onCoinsShort: (coins) => markCoinsShort(coins),
      applyPurchase: (p, req) => {
        const r = applyPurchase(activeBalance(), p, req);
        return r.ok ? r.state : null;
      },
      ...(opts.pace !== undefined ? { pace: opts.pace } : {}),
      paceScale: () => (adminTools(appStore.get()) ? ADMIN_PACE_SCALE : speedOpen(appStore.get()) ? 0.5 : 1),
      reload: opts.reload ?? (() => location.reload()),
    });
    const s = sync;
    window.addEventListener("online", () => s.online());
    // вкладки: монеты и уровень сразу, запрос идёт следом, ответ сервера решает
    connectTabs((req) => {
      void s.purchase([req]);
      optimisticPurchase(req);
    });
  }
  connectHud(
    (cmd) => game.command(cmd),
    () => void sync?.adminTool("coins"),
  );
  // шаринг (S4, `setupShare`) молчит без `kills`: кнопки окон и десктопа зовут `share({ wave })` — добавляем убийства боя
  const shareBase = windowActions.get().share;
  if (!window.__nfgWindows)
    setWindowActions({
      share: (info) => shareBase({ ...info, kills: info.kills ?? loggedKills + game.current().wave.killed }),
    });
  setWindowActions({
    chooseBoost: (i) => game.chooseBoost(i),
    nextWave: () => game.nextWave(),
    upgrade: (purchases) => {
      // сначала запрос (он запоминает состояние до покупки), затем мгновенная покупка в игре
      const done = sync?.purchase(purchases);
      game.upgrade(purchases);
      void done;
    },
    ...(sync
      ? {
          claimWelcomeBack: (claim: { offline: boolean; streak: boolean }) => void sync!.claimWelcomeBack(claim),
          claimStreak: () => void sync!.claimStreak(),
          claimTask: (task: DailyTaskClaim) => {
            void sync!.claimTask(task).then((coins) => {
              if (coins > 0) analytics().taskClaim(task, coins, appStore.get().player?.bestWave ?? 0);
            });
          },
          tasksOpened: (claimable: number) => analytics().tasksOpen(claimable),
          claimSeason: () => void sync!.claimSeason(),
          league: () => sync!.league(),
          friends: () => sync!.friends(),
          claimLeague: () => sync!.claimLeague(),
          spend: (item: ShopItem) => sync!.spend(item),
          adminTool: (action: AdminToolRequest["action"]) => sync!.adminTool(action),
        }
      : {}),
  });
  // таймер пришёл с сервера (запуск заново, исправленная волна): без окна «Штаб пал» бой начался бы сразу
  const showRetryLock = (s: AppState): void => {
    const lock = retryLockOf(s);
    if (lock && !s.route.windows.some((w) => w.kind === "defeat")) {
      openWindow({ kind: "defeat", wave: lock.wave, rollbackTo: s.player!.run.wave });
    }
  };
  // подкрепление после реванша (MADR 0019): пока сервер ждёт выбора, окно открыто и бой стоит — в том числе после
  // перезагрузки, поздней оплаты и бесплатного реванша VIP
  const showRevivePicks = (s: AppState): void => syncRevivePicks(s);
  // игрок вошёл (бой шёл на начальной забежке) или сервер закрыл забежку — начать из player.run
  let epoch = appStore.get().runEpoch;
  let hadPlayer = appStore.get().player !== null;
  let hadVip = vipActive(appStore.get());
  appStore.subscribe((s) => {
    const has = s.player !== null;
    if (s.runEpoch !== epoch || (has && !hadPlayer)) game.restart();
    epoch = s.runEpoch;
    hadPlayer = has;
    if (vipActive(s) && !hadVip) game.vipGranted();
    hadVip = vipActive(s);
    showRetryLock(s);
    showRevivePicks(s);
  });
  // игрок мог войти раньше, чем загрузился атлас: тогда стор больше не изменится
  showRetryLock(appStore.get());
  showRevivePicks(appStore.get());
  const step = game.step;
  return Object.assign(game, {
    sync,
    step: () => {
      const s = step();
      liveUpdate();
      return s;
    },
  });
}

/** Что игрок видит сейчас — для чанка эфира. */
export function liveUi(s: AppState): LiveUi {
  if (s.pause.length > 0) return { state: "hidden" };
  const w = currentWindow(s);
  if (w) return { state: "window", window: w.kind };
  if (s.boostOffer) return { state: "boost" };
  return { state: "battle" };
}
